import React from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import SectionHeader from "../components/common/SectionHeader";
import { tradingApi } from "../services/tradingApi";
import { marketDataApi } from "../services/marketDataApi";
import OrderModal from "../components/trading/OrderModal";

const OrdersPage = () => {
  const [isOrderModalOpen, setOrderModalOpen] = React.useState(false);

  const { data: orderHistory = [], isLoading: ordersLoading, error: ordersError } =
    tradingApi.endpoints.getOrderHistory.useQuery();
  const { data: marketAssets = [] } = marketDataApi.endpoints.getAllMarketAssets.useQuery();

  const [cancelOrder] = tradingApi.endpoints.cancelOrder.useMutation();

  const assetLookup = React.useMemo(() => {
    return marketAssets.reduce((acc, item) => {
      acc[item.marketAssetId] = item;
      return acc;
    }, {});
  }, [marketAssets]);

  const rows = React.useMemo(() => {
    return orderHistory.map((order) => {
      const asset = assetLookup[order.marketAssetId];
      return {
        ...order,
        symbol: asset?.symbol || `#${order.marketAssetId}`,
        assetName: asset?.name || "Unknown Asset",
      };
    });
  }, [orderHistory, assetLookup]);

  const columns = React.useMemo(
    () => [
      { field: "symbol", headerName: "Symbol", valueGetter: (row) => row.symbol },
      { field: "assetName", headerName: "Asset", valueGetter: (row) => row.assetName },
      { field: "side", headerName: "Side", valueGetter: (row) => row.side },
      { field: "type", headerName: "Type", valueGetter: (row) => row.type },
      { field: "status", headerName: "Status", valueGetter: (row) => row.status },
      {
        field: "quantity",
        headerName: "Qty",
        align: "right",
        valueGetter: (row) => Number(row.quantity || 0),
      },
      {
        field: "filledQuantity",
        headerName: "Filled",
        align: "right",
        valueGetter: (row) => Number(row.filledQuantity || 0),
      },
      {
        field: "submittedAt",
        headerName: "Submitted",
        valueGetter: (row) => row.submittedAt,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        renderCell: (row) => (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              cancelOrder(row.externalOrderId);
            }}
          >
            Cancel
          </Button>
        ),
      },
    ],
    [cancelOrder]
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <SectionHeader variant="h4" component="h1" gutterBottom sx={{ pb: 0.75 }}>
        Orders
      </SectionHeader>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Place and manage orders tied to your tracked market assets.
      </Typography>

      {ordersError ? <Alert severity="error">Failed to load order history.</Alert> : null}

      <CommonDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.orderId}
        title="All Assets"
        headerAction={
          <Button variant="contained" size="small" onClick={() => setOrderModalOpen(true)}>
            New Order
          </Button>
        }
        initialSortField="submittedAt"
        initialSortDirection="desc"
        filterPlaceholder="Filter orders by symbol, side, status, type..."
        dense={ordersLoading}
        outlined={false}
      />
      <OrderModal
        open={isOrderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        onOrderPlaced={() => setOrderModalOpen(false)}
      />
    </Box>
  );
};

export default OrdersPage;
